import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Button, CheckIcon, cn } from '@quickmd-llc/design-system';
import { isDateToday } from '@utils';
import { TimeOfDay } from '../filtersTypes';
import { useFilters } from '../useFilters';

interface Props {
  onClick: (timeOfDay: TimeOfDay) => void;
  timeOfDay: TimeOfDay;
}

export default function TimeOfDayButton({
  onClick,
  timeOfDay,
}: Props): JSX.Element {
  const [searchParams] = useSearchParams();
  const date = searchParams.get('date');
  const { timeOfDay: currentTimeOfDay } = useFilters();
  const isSelected = currentTimeOfDay === timeOfDay;
  const isDisabled = useMemo(() => {
    return getIsButtonDisabled(timeOfDay, date);
  }, [date, timeOfDay]);

  return (
    <Button
      className={cn({
        'rounded-r-none border-r-0': timeOfDay === TimeOfDay.MORNING,
        'rounded-none': timeOfDay === TimeOfDay.AFTERNOON,
        'rounded-l-none border-l-0': timeOfDay === TimeOfDay.EVENING,
      })}
      disabled={isDisabled}
      onClick={() => onClick(timeOfDay)}
      priority={isSelected ? 'primary' : 'secondary'}
      size="s"
    >
      {isSelected && <CheckIcon iconSize="l" />}
      {timeOfDay}
    </Button>
  );
}

function getIsButtonDisabled(
  timeOfDay: TimeOfDay,
  date: string | null,
): boolean {
  if (!date) {
    return false;
  }

  const d = dayjs(date);

  if (!isDateToday(d)) {
    return false;
  }

  const now = dayjs();
  const startOfDay = now.startOf('day');

  if (timeOfDay === TimeOfDay.MORNING) {
    return now.isAfter(startOfDay.add(12, 'hour'));
  }

  if (timeOfDay === TimeOfDay.AFTERNOON) {
    return now.isAfter(startOfDay.add(18, 'hour'));
  }

  return false;
}
